<template>
  <div
    v-if="open"
    class="modal-wrapper"
    @click="onClick"
  >
    <div
      v-if="shouldBeVisible"
      :class="getOverlayClass()"
      @click="onClose"
    />
    <div class="modal-helper">
      <div :class="['modal-window', modalClass]">
        <div class="modal-content">
          <modal-header-white
            v-if="showHeader"
            :device-type="deviceType"
            :label="title"
            :subtitle="subtitle"
            @closed="onClose"
          />
          <div
            v-if="showModalHeaderWhiteNav && !isDesktop"
            class="modal-header-white-nav"
          >
            <span
              class="back"
              @click="onClose"
            >
              {{ $t('header.mobileMenuHeading.back') }}
            </span>
            <a
              v-if="isMobile"
              href="/"
              class="logo"
            >
              <img
                src="~assets/images/airbaltic.svg"
                alt="airBaltic"
                width="120"
                height="29"
              >
            </a>
          </div>
          <div :class="['modal-main-part', {'with-header': showHeader}]">
            <slot>
              {{ $t('tooltip.ajaxRequestError') }}
            </slot>
            <close-icon
              v-if="showCloseIcon"
              class="independent"
              @click="onClose"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import CloseIcon from 'components/CloseIcon'
  import ModalHeaderWhite from 'components/Modal/ModalHeaderWhite'
  import { mapState, mapGetters } from 'vuex'
  import ScrollUtils from 'utils/scrollUtils'

  export default {
    components: {
      ModalHeaderWhite,
      CloseIcon
    },
    props: {
      open: {
        type: Boolean,
        required: true
      },
      showCloseIcon: {
        type: Boolean,
        required: true
      },
      title: {
        type: String,
        required: true
      },
      subtitle: {
        type: String,
        default: '',
        required: false
      },
      modalClass: {
        type: String,
        default: '',
        required: false
      },
      showModalHeader: {
        type: Boolean,
        required: true
      },
      showModalHeaderWhite: {
        type: Boolean,
        required: true
      },
      showModalHeaderWhiteNav: {
        type: Boolean,
        required: true
      },
      hideModalBackdropOnMobile: {
        type: Boolean,
        default: true
      }
    },

    computed: {
      ...mapState('main', [
        'modal'
      ]),
      ...mapState('device', [
        'deviceType'
      ]),
      ...mapGetters('device', [
        'isMobile',
        'isTablet',
        'isDesktop'
      ]),

      showHeader () {
        return this.showModalHeader || this.showModalHeaderWhite
      },

      isSafari () {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      },

      showBackdrop() {
        if (!this.isMobile) {
          return true
        }

        return !this.hideModalBackdropOnMobile;
      }
    },

    destroyed () {
      if (this.modal.modalType !== 'sidebar') {
        ScrollUtils.enableScroll()
      }
    },

    methods: {
      onClose () {
        if (this.modal.modalProps.disableCloseOnClickOutside) return
        this.$emit('update:close')

        if (this.modal.modalType !== 'sidebar') {
          ScrollUtils.enableScroll()
        }
      },

      onClick (e) {
        if (!this.isSafari) {
          return
        }

        const classList = e.target.classList
        if (classList.contains('modal-wrapper') || classList.contains('modal-window')) {
          this.onClose()
        }
      },
      shouldBeVisible () {
        return this.showBackdrop && (this.modal.modalType !== 'sidebar' || !this.isTablet)
      },
      getOverlayClass () {
        if (this.showBackdrop) {
          if (this.modal.modalType !== '') {
            return 'background-overlay-light'
          }
          return 'background-overlay'
        }

        return ''
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .modal-wrapper {
    z-index: 100;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    text-align: center;
    overflow-x: hidden;
    overflow-y: auto;
    font-family: $base-font-family;

    .l_iframe_default & {

      .modal-content {

        .modal-main-part {
          padding: 27px 23px 27px;

          @include bp-mf-tablet {
            padding: 24px 20px 24px;
          }
        }
      }
    }

    .modal-helper {
      width: 100%;
      height: 100%;

      &:before {
        content: '';
        display: inline-block;
        vertical-align: middle;
        height: 100%;
      }
    }

    .background-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #ffffff;

      @include bp-mf-tablet {
        background: rgba(11, 11, 11, .8);
      }
    }
  }

  .modal-window {
    position: relative;
    display: inline-block;
    vertical-align: middle;
    margin: 0 auto;
    width: 100%;
    height: 100%;

    @include bp-mf-tablet {
      height: auto;
    }

    &.without-body-margin .modal-main-part.with-header {
      margin-top: 0;
    }

    &.certificate-upload {

      ::v-deep .modal-header-white {
        position: relative;
        border: none !important;
        padding: 24px 55px 0 24px !important;
        height: auto;
      }

      ::v-deep .modal-title {
        color: $blue-dark;
        font-size: em(20);
        line-height: 24px;
        margin: 0 0 8px;
        text-overflow: unset;
        overflow: visible;
        white-space: normal;
      }

      ::v-deep .title-wrap {
        position: relative;
        transform: none;
      }

      ::v-deep .subtitle {
        color: rgba(21,38,73,0.64);
        font-size: em(14);
        line-height: 20px;
        width: 80%;

        @include bp-mf-tablet {
          width: 100%;
        }
      }

      ::v-deep .modal-content {
        padding-top: 0;
      }

      &.with-sidebar {

        .modal-content {
          @include bp-mf-tablet {
            width: 100%;
            padding-left: 295px;
            max-width: 845px;

            > .modal-main-part {
              padding-bottom: 40px;
            }
          }
        }
      }
    }

    &.title-size-l {
      ::v-deep .modal-title {
        font-size: em(19);
        line-height: 32px;
        color: $blue-dark;

        @include bp-mf-tablet {
          font-size: 21px;
        }
      }
    }

    &.width-auto {
      @include bp-mf-tablet {
        width: auto;
      }
    }

    &.modal-drawer {

      @include bp-mf-tablet {
        position: absolute;
        right: 0;
        height: 100%;
        width: 768px;
      }
    }
  }

  .modal-content {
    z-index: 102;
    position: relative;
    padding-top: 55px;
    width: 100%;
    min-height: 100%;
    background: #ffffff;
    text-align: left;
    line-height: 18px;

    @include bp-mf-tablet {
      margin: 0 auto;
      padding-top: 0;
      width: 550px;
      border-radius: 6px;
    }

    > .modal-main-part {
      padding: 20px;

      @include bp-mf-tablet {
        padding: 22px 24px;
      }

      ::v-deep {

        p:first-child {
          margin-top: 0;
        }

        a {
          color: $blue-medium;
        }

        ul:not(.form-errors) {
          list-style: none;

          li {
            position: relative;

            &:before {
              content: "";
              display: block;
              position: absolute;
              top: 6px;
              left: -25px;
              width: 8px;
              height: 8px;
              background-color: $green-base;
              border-radius: 50%;
            }
          }
        }

        ul:not(.form-errors):not(.dropdown-list) {
          padding-left: 40px;
        }

        .dropdown-list {
          margin: -20px 0 0 -15px;
          width: calc(100% + 30px);

          li {
            border-bottom: 1px solid #ececec;

            &:hover,
            &.selected {
              background: #f5f7cf;
            }

            &.selected {
              position: relative;

              a {
                padding-right: 50px;
              }

              &:after {
                content: $icon-checkmark;
                @include icon-font();
                position: absolute;
                top: 0;
                right: 0;
                width: 50px;
                font-size: em(12);
                line-height: 48px;
                text-align: center;
                color: $blue-medium;
              }
            }
          }

          a {
            padding: 0 15px;
            font-size: em(16);
            line-height: 48px;
            color: $grey-black;
          }
        }

        @include bp-mf-tablet {

          .sub-text {
            text-align: left;
            margin: 19px 0 24px 0;
          }

          .manage-login {
            margin-top: 10px;
          }
        }
      }
    }
  }


  @include bp-mf-tablet {

    .image-modal,
    .image-modal .modal-content {
      width: auto;
    }
  }

  .modal-white-grey {

    .modal-content,
    .modal-header-white {
      background: $secondaryForm;
    }
  }

  .modal-white-small,
  .modal-white-sidebar {

    .modal-content {
      padding-top: 0;

      @include bp-mf-tablet {
        width: 392px;
        border-radius: 6px;
      }
    }

    .close-icon {
      top: 10px;
      right: 10px;
      padding: 10px;
      font-size: em(16);
    }
  }

  .modal-white-small {

    .modal-main-part {
      padding: 20px 23px;

      @include bp-mf-tablet {
        padding: 20px;
      }
    }
  }

  .modal-white-sidebar {
    top: auto;
    margin: 0;
    width: 100%;
    min-height: 100%;
    transform: none;
    border-radius: 0;

    @include bp-mf-tablet {
      min-height: inherit;
    }

    .modal-content {
      margin: 0;

      @include bp-mf-tablet {
        margin: 0 auto;
      }

      .modal-main-part {
        padding: 20px;

        @include bp-mf-tablet {
          padding: 32px 28px 40px;
        }

        &.with-header {
          padding: 87px 23px 27px;

          @include bp-mf-tablet {
            padding: 24px 20px 24px;
          }
        }
      }
    }
  }

  .multi-line {

    .modal-content {
      display: flex;
      flex-direction: column;
      padding-top: 0;

      @include bp-mf-tablet {
        margin-bottom: 200px;
      }

      ::v-deep .modal-header-white {
        border: none;
        height: auto;
        position: static;

        .title-wrap {
          position: static;
          transform: translate(0,0);
        }

        .modal-title {
          padding-top: 24px;
          @include defaultFontValues($blue-dark, 16, 19, 500);
        }

        .subtitle {
          @include defaultFontValues($blue-secondary, 14, 20);
          margin: 12px 0 6px;
          max-width: 320px;
        }
      }
    }
  }


  .modal-white-centered {

    .modal-content {
      padding-top: 80px;
      width: 100%;
      height: 100%;
      max-height: 100%;
      overflow: auto;
      border-radius: 0;

      @include bp-mf-tablet {
        padding-top: 0;
        max-height: 704px;
        overflow: hidden;
        border-radius: 10px;
      }

      @include bp-mf-tablet {
        width: 550px;
      }
    }

    &.wide {

      .modal-content {

        @include bp-mf-tablet {
          width: 740px;
          border-radius: 6px;
        }

        .close-icon {

          @include bp-mf-tablet {
            font-size: em(18);
            top: 14px;
            right: 12px;
          }
        }
      }

      .modal-main-part {
        padding: 23px 20px;

        @include bp-mf-tablet {
          padding: 40px 36px 52px;
        }
      }
    }

    .modal-main-part {
      padding: 0 23px;

      @include bp-mf-tablet {
        padding: 0 33px;
        max-height: 669px;
        overflow-y: auto;
      }

      ::v-deep .wysiwyg {
        display: block;
        overflow-y: auto;

        @supports (-webkit-overflow-scrolling: touch) {
          overflow-y: inherit;
        }

        @include bp-mf-tablet {
          position: relative;
          margin-left: -33px;
          padding: 0 33px;
          width: calc(100% + 66px);
          max-height: 624px;
        }

        h4 {
          position: fixed;
          top: 0;
          left: 0;
          margin: 0;
          padding: 0 55px 0 23px;
          width: 100%;
          height: 60px;
          background-color: #ffffff;
          border-bottom: 1px solid $grey-border;
          @include text-overflow();
          font-size: em(16);
          line-height: 56px;
          font-weight: 500;

          @include bp-mf-tablet {
            top: inherit;
            left: inherit;
            margin-left: -33px;
            padding-left: 33px;
            font-size: em(17);
          }

          @include bp-mf-tablet {
            width: 550px;
          }
        }

        p {
          margin: 0 0 12px;
          line-height: 20px;

          @include bp-mf-tablet {
            font-size: em(16);
            line-height: 24px;
          }

          &:first-of-type {

            @include bp-mf-tablet {
              padding-top: 20px;
            }
          }

          &:last-child:not(:first-of-type) {
            margin-bottom: 30px;
          }
        }

        b,
        strong {
          font-weight: 500;
        }

        a {
          color: $blue-medium;

          &:hover {

            @include bp-mf-tablet {
              color: $blue-light;
            }
          }
        }
      }
    }
  }

  .modal-header-white-nav {
    position: fixed;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 61px;
    background-color: white;
    border-bottom: 1px solid $grey-border;
  }

  .logo {
    float: right;
    width: 126px;
    height: 100%;

    img {
      display: block;
      position: relative;
      top: calc(50% - 9px);
      margin: 0 auto;
      width: auto;
      height: 18px;
    }
  }

  .back {
    padding: 0 23px;
    line-height: 63px;
    font-weight: 500;
    color: $blue-medium;
    cursor: pointer;

    &:before {
      content: $icon-arrow-left-thin;
      @include icon-font-thin();
      margin-right: 13px;
      vertical-align: middle;
      font-size: em(20);
    }
  }

  .close-icon.independent {
    position: fixed;
    top: 10px;
    right: 10px;
    padding: 12px;
    font-size: em(16);
    color: $blue-medium;

    @include bp-mf-tablet {
      position: absolute;
      font-size: em(18);
      padding: 10px;
      top: 12px;
      right: 12px;
    }

    &:hover {

      @include bp-mf-tablet {
        color: $blue-light;
      }
    }
  }

  .scn-modal {
    .modal-content {
      padding: 55px 16px 16px;

      @include bp-mf-tablet {
        width: 692px;
        padding: 32px;
      }
    }

    ::v-deep .modal-header-white {
      padding: 0 55px 0 16px;
      border-bottom: none;

      @include bp-mf-tablet {
        padding: 0;
      }

      .modal-title {
        color: #152649;
        font-size: em(21);
        line-height: em(32);
      }
    }

    .modal-main-part {
      padding: 0;
    }

    ::v-deep .modal-header-white .close-icon {
      top: 16px;
      right: 16px;
      line-height: 25px;
      color: #152649;

      @include bp-mf-tablet {
        right: 0;
        top: 0;
      }
    }
  }

  .promo-popup-modal {
    height: auto;

    .modal-content {
      padding: 0;
      background: none;

      @include bp-mf-tablet {
        width: 480px;
      }

      .modal-main-part {
        padding: 0;

        ::v-deep {
          a {
            color: white !important;
          }
        }
      }
    }
  }

  .modal-date-change {
    .modal-content {
      @include bp-mf-tablet {
        width: 90%;
        max-width: 1200px;
      }
    }
  }

  .hide-close-icon {
    ::v-deep .close-icon {
      display: none;
    }
  }

  .modal-drawer {
    .modal-content {
      @include bp-mf-tablet {
        width: 100%;
        border-radius: 8px 0 0 8px;
      }
    }

    .modal-main-part {
      @include bp-mf-tablet {
        padding: 24px 40px;
      }
    }
  }

  .modal-company-details {
    .modal-content {
      @include bp-mf-tablet {
        width: 736px;
      }
    }
  }
</style>
